import Vue from "vue";
import TableScrollNavigator from "@/components/UI/TableScrollNavigator.vue";
import i18n from "@/i18n";
import store from "@/store";
import vuetify from "@/plugins/vuetify";

const TableScrollNavigatorConstructor = Vue.extend(TableScrollNavigator);

/**
 * Helper to mount TableScrollNavigator into a container or alongside a v-data-table
 */
export function attachTableNavigator(
  containerOrTable,
  options = {},
  contextVm = null
) {
  if (!containerOrTable || containerOrTable.__tableNavMounted) return null;

  const wrapper = containerOrTable.classList.contains("v-data-table__wrapper")
    ? containerOrTable
    : containerOrTable.querySelector(".v-data-table__wrapper") ||
      containerOrTable;

  if (!wrapper) return null;

  // Mount component with full context
  const navInstance = new TableScrollNavigatorConstructor({
    i18n: contextVm?.$root?.$options?.i18n || contextVm?.$i18n || i18n,
    store: contextVm?.$root?.$options?.store || contextVm?.$store || store,
    vuetify: contextVm?.$root?.$options?.vuetify || vuetify,
    parent: contextVm || undefined,
    propsData: {
      target: wrapper,
      ...options,
    },
  });

  const mountPoint = document.createElement("div");
  mountPoint.className = "table-nav-mount-point";

  const parent = containerOrTable.classList.contains("magic-table-wrapper")
    ? containerOrTable
    : containerOrTable.closest(".magic-table-wrapper") ||
      wrapper.parentElement ||
      containerOrTable;

  // Insert at the TOP of the parent: before .v-data-table or at start of parent
  const vDataTable = parent.querySelector(".v-data-table") || wrapper;
  if (vDataTable && vDataTable.parentElement === parent) {
    parent.insertBefore(mountPoint, vDataTable);
  } else if (parent.firstChild) {
    parent.insertBefore(mountPoint, parent.firstChild);
  } else {
    parent.appendChild(mountPoint);
  }
  navInstance.$mount(mountPoint);

  containerOrTable.__tableNavMounted = true;
  containerOrTable.__tableNavInstance = navInstance;

  return navInstance;
}

/**
 * Global Directive: v-table-scroll
 * Usage: <v-data-table v-table-scroll ... /> or <div class="magic-table-wrapper" v-table-scroll>
 */
export const TableScrollDirective = {
  inserted(el, binding, vnode) {
    Vue.nextTick(() => {
      attachTableNavigator(el, binding.value || {}, vnode?.context);
    });
  },
  unbind(el) {
    if (el.__tableNavInstance) {
      el.__tableNavInstance.$destroy();
      if (el.__tableNavInstance.$el && el.__tableNavInstance.$el.parentNode) {
        el.__tableNavInstance.$el.parentNode.removeChild(
          el.__tableNavInstance.$el
        );
      }
      el.__tableNavInstance = null;
      el.__tableNavMounted = false;
    }
  },
};

/**
 * Global Table Scroll Plugin
 */
export default {
  install(Vue) {
    Vue.component("TableScrollNavigator", TableScrollNavigator);
    Vue.directive("table-scroll", TableScrollDirective);

    // Global Auto-Enhancer Mixin
    // Automatically auto-enhances .magic-table-wrapper and .v-data-table in all views
    Vue.mixin({
      mounted() {
        this.$nextTick(() => {
          this.autoAttachTableNavigators();
        });
      },
      updated() {
        this.$nextTick(() => {
          this.autoAttachTableNavigators();
        });
      },
      beforeDestroy() {
        if (this.$el && this.$el.querySelectorAll) {
          const tables = this.$el.querySelectorAll(
            ".magic-table-wrapper, .v-data-table"
          );
          tables.forEach((table) => {
            if (table.__tableNavInstance) {
              table.__tableNavInstance.$destroy();
              if (
                table.__tableNavInstance.$el &&
                table.__tableNavInstance.$el.parentNode
              ) {
                table.__tableNavInstance.$el.parentNode.removeChild(
                  table.__tableNavInstance.$el
                );
              }
              table.__tableNavInstance = null;
              table.__tableNavMounted = false;
            }
          });
        }
      },
      methods: {
        autoAttachTableNavigators() {
          if (!this.$el || !this.$el.querySelectorAll) return;

          // Target magic-table-wrapper first, or v-data-table
          const wrappers = this.$el.querySelectorAll(".magic-table-wrapper");
          if (wrappers.length > 0) {
            wrappers.forEach((wrapper) => {
              if (!wrapper.__tableNavMounted) {
                attachTableNavigator(wrapper, {}, this);
              }
            });
          } else {
            const dataTables = this.$el.querySelectorAll(".v-data-table");
            dataTables.forEach((table) => {
              if (!table.__tableNavMounted) {
                attachTableNavigator(table, {}, this);
              }
            });
          }
        },
      },
    });
  },
};
